import { RunFilesType } from '@/hooks/api/runs/useDownloadRunFilesZip.ts';

export const get_project_suites_url = (projectId: string) =>
  `/projects/${projectId}/automation-suites`;
export const get_automation_suite_url = (suiteId: string) => `/automation-suites/${suiteId}`;

export const get_llm_model_options_url = (): string => {
  return `/llm/model-options`;
};

export const get_automation_capabilities_url = (stepType?: string): string => {
  return stepType ? `/automation-capabilities?step_type=${encodeURIComponent(stepType)}` : `/automation-capabilities`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const create_project_url = (orgId?: string): string => {
  return `/organizations/${orgId}/projects`;
};

export const get_projects_by_organization_url = (): string => {
  return `/projects/by_organization`;
};

export const get_project_url = (projectId?: string): string => {
  return `/projects/${projectId}`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_automations_url = (projectId?: string): string => {
  return get_project_url(projectId) + `/automations`;
};

export const get_automation_url = (automationId?: string): string => {
  return `/automations/${automationId}`;
};

export const get_automation_portal_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/portal`;
};

export const regenerate_automation_portal_link_url = (automationId?: string): string => {
  return get_automation_portal_url(automationId) + `/regenerate-link`;
};

export const get_automation_portal_logo_url = (automationId?: string): string => {
  return get_automation_portal_url(automationId) + `/logo`;
};

export const get_automation_portal_runs_url = (automationId?: string): string => {
  return get_automation_portal_url(automationId) + `/runs`;
};

export const get_automation_versions_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/versions`;
};

export const get_automation_latest_version_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/latest-version`;
};

export const get_automation_discard_latest_draft_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/discard-latest-draft`;
};

export const get_editor_composition_capabilities_url = (
  automationId?: string,
  subflowId?: string,
  stepId?: string,
): string => {
  const params = new URLSearchParams({ subflow_id: subflowId ?? '' });
  if (stepId) params.set('step_id', stepId);
  return `${get_automation_url(automationId)}/editor-composition-capabilities?${params.toString()}`;
};

export const get_editor_local_subflow_context_url = (
  automationId?: string,
  subflowId?: string,
): string => {
  const params = new URLSearchParams({ subflow_id: subflowId ?? '' });
  return `${get_automation_url(automationId)}/editor-local-subflow-context?${params.toString()}`;
};

export const get_editor_local_subflow_guarded_field_url = (
  automationId?: string,
  subflowId?: string,
  stepId?: string,
  fieldName: 'provided_code' = 'provided_code',
): string => {
  const params = new URLSearchParams({
    subflow_id: subflowId ?? '',
    step_id: stepId ?? '',
    field_name: fieldName,
  });
  return `${get_automation_url(automationId)}/editor-local-subflow-guarded-field?${params.toString()}`;
};

export const get_automation_revalidate_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/revalidate`;
};

export const get_automation_llm_usage_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/llm-usage`;
};

export const get_automation_retirement_preflight_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/retirement-preflight`;
};

export const get_automation_retire_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/retire`;
};

export const get_automation_unretire_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/unretire`;
};

export const get_automation_version_url = (versionId?: string): string => {
  return `/automation-versions/${versionId}`;
};

export const get_automation_version_documentation_url = (versionId?: string): string =>
  get_automation_version_url(versionId) + '/documentation';

export const get_automation_version_schemas_url = (versionId?: string): string => {
  return get_automation_version_url(versionId) + `/schemas`;
};

export const get_automation_version_test_suite_url = (versionId?: string): string =>
  get_automation_version_url(versionId) + '/test-suite';

export const get_automation_builder_version_test_suite_url = (
  automationId?: string,
  versionId?: string,
): string => `${get_automation_url(automationId)}/automation-versions/${versionId}/builder-test-suite`;

export const get_automation_test_fixtures_url = (automationId?: string): string =>
  get_automation_url(automationId) + '/test-fixtures';

export const get_automation_test_fixture_retire_url = (
  automationId?: string,
  fixtureId?: string,
): string => `${get_automation_test_fixtures_url(automationId)}/${fixtureId}/retire`;

export const get_automation_test_fixture_inputs_url = (
  automationId?: string,
  versionId?: string,
): string => `${get_automation_url(automationId)}/automation-versions/${versionId}/test-fixture-inputs`;

export const preview_automation_test_fixture_inputs_url = (
  automationId?: string,
  versionId?: string,
): string => `${get_automation_test_fixture_inputs_url(automationId, versionId)}/preview`;

export const get_automation_version_test_fixture_binding_url = (
  automationId?: string,
  versionId?: string,
  fixtureId?: string,
): string => `${get_automation_url(automationId)}/automation-versions/${versionId}/test-fixtures/${fixtureId}/binding`;

export const preview_automation_version_test_suite_url = (versionId?: string): string =>
  `${get_automation_version_test_suite_url(versionId)}/preview`;

export const create_automation_version_test_case_plan_url = (
  automationId?: string,
  versionId?: string,
  caseId?: string,
): string => `${get_automation_url(automationId)}/automation-versions/${versionId}/builder-test-cases/${caseId}/plan`;

export const get_automation_version_test_case_runs_url = (versionId?: string, caseId?: string): string =>
  `${get_automation_version_test_suite_url(versionId)}/cases/${caseId}/runs`;

export const create_automation_version_test_case_run_from_plan_url = (
  automationId?: string,
  versionId?: string,
  caseId?: string,
): string => `${get_automation_url(automationId)}/automation-versions/${versionId}/builder-test-cases/${caseId}/runs/from-plan`;

export const get_automation_version_comparison_v3_url = (versionId?: string): string =>
  get_automation_version_url(versionId) + '/comparison-v3';

export const get_automation_version_comparison_v3_detail_url = (versionId?: string, subjectRef?: string): string =>
  get_automation_version_comparison_v3_url(versionId) + `/${subjectRef}`;

export const create_automation_version_comparison_v3_summary_url = (versionId?: string): string =>
  get_automation_version_comparison_v3_url(versionId) + '/summary';

export const get_automation_version_dependencies_url = (versionId?: string): string => {
  return get_automation_version_url(versionId) + `/dependencies`;
};

export const get_composition_compile_preview_url = (automationId?: string): string => {
  return get_automation_url(automationId) + '/composition/compile-preview';
};

export const get_composition_apply_operations_url = (automationId?: string): string => {
  return get_automation_url(automationId) + '/composition/apply-operations';
};

export const get_automation_version_contract_publish_lifecycle_url = (versionId?: string): string => {
  return get_automation_version_url(versionId) + `/contract-publish-lifecycle`;
};

export const revert_automation_version_url = (versionId?: string): string => {
  return get_automation_version_url(versionId) + `/revert`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_integrations_session_url = (): string => {
  return `/integrations/create-session`;
};

export const get_integrations_url = (projectId?: string): string => {
  return `/projects/${projectId}/integrations`;
};

export const get_integration_url = (integrationId?: string): string => {
  return `/integrations/${integrationId}`;
};

export const get_integration_files_url = (integrationId?: string): string => {
  return get_integration_url(integrationId) + `/files`;
};

export const get_integration_config_step_action_url = (
  automationId: string,
  stepId: string,
): string => {
  return `/automations/${automationId}/steps/${stepId}/action`;
};

export const get_integration_config_select_field_options_url = (
  automationId: string,
  stepId: string,
  fieldId: string,
): string => {
  return `/automations/${automationId}/steps/${stepId}/config-fields/${fieldId}/options`;
};

export const get_integration_config_field_sensitive_value_url = (
  automationId: string,
  stepId: string,
  fieldId: string,
): string => {
  return `/automations/${automationId}/steps/${stepId}/config-fields/${fieldId}/sensitive-value`;
};

export const get_email_routes_url = (automationId: string, stepId: string): string => {
  return `/automations/${automationId}/steps/${stepId}/email-routes`;
};

export const get_automation_email_identity_url = (automationId: string): string => {
  return `/automations/${automationId}/email-identity`;
};

export const get_email_aliases_url = (automationId: string, stepId: string): string => {
  return `/automations/${automationId}/steps/${stepId}/email-aliases`;
};

export const get_email_alias_url = (
  automationId: string,
  stepId: string,
  routeId: string,
): string => {
  return `${get_email_aliases_url(automationId, stepId)}/${routeId}`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_project_runs_stats_url = (projectId?: string): string => {
  return get_project_url(projectId) + `/runs/stats`;
};

export const get_project_runs_url = (projectId?: string): string => {
  return get_project_url(projectId) + `/runs`;
};

export const get_automation_runs_stats_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/runs/stats`;
};

export const get_automation_runs_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/runs`;
};

export const get_automation_runs_with_datasets_url = (automationId?: string): string => {
  return get_automation_runs_url(automationId) + `/with_datasets`;
};

export const get_run_url = (runId?: string): string => {
  return `/runs/${runId}`;
};

export const get_run_human_decisions_url = (runId?: string): string => {
  return get_run_url(runId) + `/human-decisions`;
};

export const get_project_human_decisions_url = (projectId?: string): string => {
  return `/projects/${projectId}/human-decisions`;
};

export const resolve_human_decision_url = (decisionId: string): string => {
  return `/human-decisions/${decisionId}/resolve`;
};

export const human_decision_action_url = (
  decisionId: string,
  action: 'reject' | 'cancel',
): string => {
  return `/human-decisions/${decisionId}/${action}`;
};

export const human_decision_claim_url = (decisionId: string): string => {
  return `/human-decisions/${decisionId}/claim`;
};

export const human_decision_release_claim_url = (decisionId: string): string => {
  return `/human-decisions/${decisionId}/release-claim`;
};

export const run_latest_execution_url = (runId?: string): string => {
  return get_run_url(runId) + `/latest-execution`;
};

export const create_manual_run_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/runs/manual`;
};

export const run_automation_version_url = (runId?: string): string => {
  return get_run_url(runId) + `/automation-version`;
};

export const run_chat_messages_url = (runId?: string): string => {
  return get_run_url(runId) + `/chat`;
};

export const cancel_run_chat_messages_url = (runId?: string): string => {
  return get_run_url(runId) + `/chat/cancel`;
};

export const cancel_run_executions_url = (runId?: string): string => {
  return get_run_url(runId) + `/cancel-executions`;
};

export const reexecute_run_url = (runId?: string): string => {
  return get_run_url(runId) + `/reexecute`;
};

export const intervention_response_url = (runId?: string, stepRunId?: string): string => {
  return get_run_url(runId) + `/step-runs/${stepRunId}/intervention-response`;
};

export const reprocess_inputs_and_create_new_run_url = (runId?: string): string => {
  return get_run_url(runId) + `/reprocess-inputs-and-create-new-run`;
};

export const replay_run_from_source_url = (runId?: string): string => {
  return get_run_url(runId) + `/replay-from-source`;
};

export const get_run_latest_step_runs_url = (runId?: string): string => {
  return get_run_url(runId) + `/step-runs/latest`;
};

export const get_run_auto_expand_plan_url = (runId?: string): string => {
  return get_run_url(runId) + `/step-runs/auto-expand-plan`;
};

export const get_run_all_step_runs_url = (runId?: string): string => {
  return get_run_url(runId) + `/step-runs/all`;
};

export const get_run_retry_presentation_url = (runId?: string, stepRunId?: string): string => {
  return get_run_url(runId) + `/step-runs/${stepRunId}/retry-presentation`;
};

export const get_run_waiting_step_dependencies_url = (runId?: string): string => {
  return get_run_url(runId) + `/step-runs/waiting-dependencies`;
};

export const get_run_llm_usage_url = (runId?: string): string => {
  return get_run_url(runId) + `/llm-usage`;
};

export const get_run_test_outcome_url = (runId?: string): string => get_run_url(runId) + '/test-outcome';

export const get_automation_builder_readiness_url = (
  automationId?: string,
  versionId?: string,
): string => {
  const query = versionId ? `?automation_version_id=${encodeURIComponent(versionId)}` : '';
  return get_automation_url(automationId) + `/builder-readiness${query}`;
};

export const get_run_step_debug_preview_url = ({
  runId,
  stepId,
  fullPath,
  stepRunId,
  maxRows = 25,
  maxCellChars = 500,
  maxIterations = 50,
}: {
  runId?: string;
  stepId?: string;
  fullPath?: string | null;
  stepRunId?: string;
  maxRows?: number;
  maxCellChars?: number;
  maxIterations?: number;
}): string => {
  const params = new URLSearchParams({
    max_rows: String(maxRows),
    max_cell_chars: String(maxCellChars),
    max_iterations: String(maxIterations),
  });
  if (fullPath) params.set('full_path', fullPath);
  if (stepRunId) params.set('step_run_id', stepRunId);
  return get_run_url(runId) + `/steps/${stepId}/debug-preview?${params.toString()}`;
};

export const get_run_step_history_url = ({
  runId,
  stepId,
  fullPath,
  stepRunId,
}: {
  runId?: string;
  stepId?: string;
  fullPath?: string | null;
  stepRunId?: string;
}): string => {
  const params = new URLSearchParams();
  if (fullPath) params.set('full_path', fullPath);
  if (stepRunId) params.set('step_run_id', stepRunId);
  const query = params.toString();
  return get_run_url(runId) + `/steps/${stepId}/history${query ? `?${query}` : ''}`;
};

export const get_run_files_presigned_url = (runId: string, filesType: RunFilesType): string => {
  return get_run_url(runId) + `/urls-for-files?files_type=${filesType}`;
};

export const get_automation_version_local_test_evidence_url = (versionId: string): string => {
  return `${get_automation_version_url(versionId)}/local-test-evidence`;
};

export const get_automation_version_local_test_receipt_url = (
  versionId: string,
  requestId: string,
): string => {
  return `${get_automation_version_local_test_evidence_url(versionId)}/receipts/${requestId}`;
};

export const get_automation_version_local_test_artifact_download_url = (
  versionId: string,
  requestId: string,
  role: string,
): string => {
  return `${get_automation_version_local_test_evidence_url(versionId)}/receipts/${requestId}/artifacts/${role}/download`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_step_execution_url = (stepExecutionId?: string): string => {
  return `/step-executions/${stepExecutionId}`;
};

export const get_step_execution_metadata_url = (stepExecutionId?: string): string => {
  return `/step-executions/${stepExecutionId}/metadata`;
};

export const get_step_run_llm_usage_url = (stepRunId?: string): string => {
  return `/step-runs/${stepRunId}/llm-usage`;
};

export const get_step_run_connections_url = (stepRunId?: string): string => {
  return `/step-runs/${stepRunId}/connections`;
};

export const get_step_run_log_presentation_url = (stepRunId?: string): string => {
  return `/step-runs/${stepRunId}/log-presentation`;
};

export const get_step_execution_logs_url = (stepExecutionId?: string): string => {
  return `/step-executions/${stepExecutionId}/logs`;
};

// export const get_step_execution_child_logs_url = (stepExecutionId?: string): string => {
//   return `/step-executions/${stepExecutionId}/child-logs`;
// };
//
// export const get_step_execution_extract_error_logs_url = (stepExecutionId?: string): string => {
//   return `/step-executions/${stepExecutionId}/extract-errors`;
// };

export const get_extract_entity_error_logs_url = (entityId?: string): string => {
  return `/extract-entities/${entityId}/logs`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_extract_entities_url = (stepExecutionId?: string): string => {
  return `/step-executions/${stepExecutionId}/extract-entities`;
};

export const get_extract_entity_fields_url = (entityId?: string): string => {
  return `/extract-entities/${entityId}/fields`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_add_step_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/add-step`;
};

export const get_update_step_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/update-step`;
};

export const get_delete_step_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/delete-step`;
};

export const get_duplicate_step_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/duplicate-step`;
};

export const get_reorder_steps_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/reorder-steps`;
};

export const get_upstream_data_outputs_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/upstream-data-outputs`;
};

export const get_children_data_outputs_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/children-data-outputs`;
};

export const get_output_contracts_url = (versionId?: string): string => {
  return `${get_automation_version_url(versionId)}/output-contracts`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_automation_static_datasets_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/datasets/static`;
};

export const get_automation_static_file_datasets_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/datasets/static/file`;
};

export const get_automation_static_directory_datasets_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/datasets/static/directory`;
};

export const get_static_dataset_url = (datasetId?: string): string => {
  return `/datasets/static/${datasetId}`;
};

export const get_static_dataset_replacement_file_url = (staticDatasetId?: string): string => {
  return `${get_static_dataset_url(staticDatasetId)}/replacement/file`;
};

export const get_static_dataset_replacement_directory_url = (staticDatasetId?: string): string => {
  return `${get_static_dataset_url(staticDatasetId)}/replacement/directory`;
};

export const get_static_dataset_replacement_commit_url = (
  staticDatasetId?: string,
  datasetId?: string,
): string => {
  return `${get_static_dataset_url(staticDatasetId)}/replacement/${datasetId}/commit`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_automation_trigger_datasets_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/datasets/triggers`;
};

export const get_run_with_datasets_url = (runId?: string): string => {
  return get_run_url(runId) + `/with_datasets`;
};

/////////////////////////////////////////////////////////////////////////////////////////////

export const get_dataset_url = (datasetId?: string): string => {
  return `/datasets/${datasetId}`;
};

export const get_process_dataset_url = (datasetId?: string): string => {
  return `${get_dataset_url(datasetId)}/process`;
};

export const get_dataset_notes_url = (datasetId?: string): string => {
  return `/datasets/${datasetId}/notes`;
};

export const get_dataset_extract_execution_url = (datasetId?: string): string => {
  return `/datasets/${datasetId}/extract-execution`;
};

export const get_dataset_relevant_steps_url = (datasetId?: string): string => {
  return `${get_dataset_url(datasetId)}/relevant-steps`;
};

export const get_dataset_parent_url = (datasetId?: string): string => {
  return `/datasets/${datasetId}/parent`;
};

export const get_child_datasets_url = (datasetId?: string): string => {
  return `/datasets/${datasetId}/child-datasets`;
};

export const get_dataset_presigned_url = (datasetId?: string): string => {
  return `/datasets/${datasetId}/presigned-url`;
};

export const get_automation_linked_datasets_url = (automationId?: string): string => {
  return get_automation_url(automationId) + `/datasets/linked`;
};

export const get_automation_linked_dataset_from_url_url = (automationId?: string): string => {
  return get_automation_linked_datasets_url(automationId) + `/from-url`;
};

export const get_linked_dataset_url = (datasetId?: string): string => {
  return `/datasets/linked/${datasetId}`;
};

// export const get_linked_dataset_latest_dataset_url = (datasetId?: string): string => {
//   return `/datasets/linked/${datasetId}/latest-dataset`;
// };

export const get_static_dataset_usage_url = (automationId: string) =>
  `${get_automation_static_datasets_url(automationId)}/usage`;
